// src/components/ui/Snackbar.tsx — SCE Issues
// Snackbar auto-disparition conforme design system Sartorius

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { CheckCircle2, AlertCircle, Info } from 'lucide-react'

export type SnackbarType = 'success' | 'error' | 'info'

interface SnackbarState {
  message: string
  type: SnackbarType
  id: number
}

interface SnackbarContextValue {
  show: (message: string, type?: SnackbarType) => void
}

const SnackbarContext = createContext<SnackbarContextValue | null>(null)

// ─── Provider ───

export function SnackbarProvider({ children }: { children: ReactNode }): JSX.Element {
  const [snackbar, setSnackbar] = useState<SnackbarState | null>(null)

  const show = useCallback((message: string, type: SnackbarType = 'info'): void => {
    setSnackbar({ message, type, id: Date.now() })
  }, [])

  // Auto-disparition (guideline Sartorius : 4-10s). Une erreur reste 8s :
  // elle se lit, et souvent se relit, avant d'agir — une confirmation se
  // survole.
  useEffect(() => {
    if (!snackbar) return
    const timer = setTimeout(() => setSnackbar(null), snackbar.type === 'error' ? 8000 : 4000)
    return () => clearTimeout(timer)
  }, [snackbar])

  return (
    <SnackbarContext.Provider value={{ show }}>
      {children}
      {snackbar && <SnackbarView {...snackbar} />}
    </SnackbarContext.Provider>
  )
}

// ─── Hook consommateur ───

export function useSnackbar(): SnackbarContextValue {
  const ctx = useContext(SnackbarContext)
  if (!ctx) {
    throw new Error('useSnackbar doit être utilisé dans un SnackbarProvider')
  }
  return ctx
}

// ─── Vue ───

function SnackbarView({ message, type }: SnackbarState): JSX.Element {
  const config = {
    success: {
      bg: 'var(--color-traffic-green)',
      color: 'var(--color-white)',
      Icon: CheckCircle2,
    },
    error: {
      bg: 'var(--color-traffic-red)',
      color: 'var(--color-white)',
      Icon: AlertCircle,
    },
    info: {
      bg: 'var(--color-black)',
      color: 'var(--color-white)',
      Icon: Info,
    },
  }[type]

  const { Icon } = config

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-24 left-1/2 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg"
      style={{
        // Au-dessus de TOUT : modale de fiche (60), tiroirs (61), infobulles
        // et visionneuse photo (80). À z-50, un message d'erreur levé depuis
        // une fiche en surimpression s'affichait SOUS le voile — invisible.
        zIndex: 100,
        backgroundColor: config.bg,
        color: config.color,
        maxWidth: 'calc(100vw - 32px)',
        minWidth: 260,
        transform: 'translateX(-50%)',
        animation: 'slideUp 0.25s ease-out',
        fontFamily: 'var(--font-family)',
        fontSize: 'var(--font-size-copy2)',
      }}
    >
      <Icon size={20} strokeWidth={2} />
      <span>{message}</span>
    </div>
  )
}
